import React, { useState } from "react";
import {
  IConfirmCcaStudentRenewalButton,
  useStyles,
} from "./ConfirmCcaStudentRenewalButton";
import {
  Button,
  useDataProvider,
  useMutation,
  useNotify,
  useRefresh,
} from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import CustomDatePickerOutsideFormInput from "components/CustomDatePickerOutsideFormInput";
import DoneIcon from "@material-ui/icons/Done";

const getMinDate = (startDate: string) => {
  const ccaAcademicYearStartDate = new Date(startDate);
  const today = new Date();

  // If academicYearStartDate is equals after today, use academicYearStartDate as the min date
  if (ccaAcademicYearStartDate >= today) {
    return ccaAcademicYearStartDate.toISOString().split("T")[0];
  }

  // Otherwise, use today's date as the min date
  return today.toISOString().split("T")[0];
};

const AcceptButton = ({
  id,
  studentId,
  ccaAcademicYearId,
}: IConfirmCcaStudentRenewalButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [loadingProcess, setLoadingProcess] = useState<boolean>(false);
  const [ccaAcademicYear, setCcaAcademicYear] = useState<IAcademicYear>();
  const [selectedDate, setSelectedDate] = useState<string>(null);

  const classes = useStyles();
  const provider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleClickButton = (ccaAcademicYearId: number) => {
    setLoadingProcess(true);
    provider
      .getOne("ccaAcademicYears", {
        id: ccaAcademicYearId,
      })
      .then(({ data }) => {
        if (new Date() > new Date(data.earlyRegistrationEndDate)) {
          setCcaAcademicYear(data as IAcademicYear);
          handleOpenDialog();
        } else {
          handleAcceptStudentRenewal();
        }

        setLoadingProcess(false);
      });
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [handleAcceptStudentRenewal, { loading }] = useMutation(
    {
      type: "update",
      resource: `ccaStudentRenewals`,
      payload: {
        id: id,
        data: {
          isAcceptCcaRenewal: true,
          studentId: studentId,
          ccaAcademicYearId: ccaAcademicYearId,
          ccaScheduleStartDate: selectedDate,
        },
      },
    },
    {
      onSuccess: () => {
        notify(`Successfully accept student renewal.`, "info");
        refresh();
      },
      onFailure: (error) =>
        notify(
          `Failed accept student renewal, error: ${error.message}`,
          "warning"
        ),
    }
  );

  return (
    <>
      <Button
        onClick={() => handleClickButton(ccaAcademicYearId)}
        color="primary"
        label={loading || loadingProcess ? "Please wait..." : "Accept"}
        autoFocus
      >
        <DoneIcon />
      </Button>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialogSetDate}
        maxWidth="sm"
      >
        {ccaAcademicYear && (
          <>
            <DialogTitle id="alert-dialog-title">
              CCA Academic Year {ccaAcademicYear?.name} Information
            </DialogTitle>
            <DialogContent>
              <DialogContentText color="initial">
                <Typography variant="body1" gutterBottom>
                  The Early Registration period has been passed.
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Please accept the renewal with select a date to set as the
                  student's schedule.
                </Typography>
                <CustomDatePickerOutsideFormInput
                  name="ccaScheduleStartDate"
                  label="Schedule Start Date"
                  min={getMinDate(ccaAcademicYear.startDate)}
                  max={ccaAcademicYear.endDate}
                  parentCallback={(date: string) => {
                    setSelectedDate(date);
                  }}
                  isWeekDays
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                label="Back"
                style={{ backgroundColor: "#9e9e9e" }}
              />
              <Button
                onClick={handleAcceptStudentRenewal}
                color="primary"
                label={loading || loadingProcess ? "Please wait..." : "Accept"}
                disabled={selectedDate === null}
                autoFocus
              >
                <DoneIcon />
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AcceptButton;
